/**
 * ArchivedAbortedVoyagesTab.js
 *
 * FT-269: new tab in Voyage Management listing:
 *   - DRAFT voyages where (now - lastDraftUpdatedAt) > 24h (auto-archived
 *     by UI filter only; status remains DRAFT until the user explicitly
 *     cancels, preserving ISM Code retention).
 *   - ABORTED voyages (terminal; no actions besides display).
 *
 * Wired into VoyageManagementTab as the third tab (Active / Recent /
 * Archived & Aborted).
 */

import React, { useMemo } from 'react';
import { Archive, RefreshCcw, Ban } from 'lucide-react';
import { VOYAGE_STATUS } from '../../../../config/operationsConfig';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  return `${Math.round(diff / 86_400_000)} d ago`;
}

const StatusBadge = ({ status }) => {
  if (status === VOYAGE_STATUS.DRAFT) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" aria-label="Draft (auto-archived)">
        Draft
      </span>
    );
  }
  if (status === VOYAGE_STATUS.ABORTED) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700" aria-label="Aborted">
        Aborted
      </span>
    );
  }
  return null;
};

const ArchivedAbortedVoyagesTab = ({ voyages = [], onResume, onCancelPermanently }) => {
  const rows = useMemo(() => {
    const now = Date.now();
    const archived = voyages.filter(v => {
      if (v.status === VOYAGE_STATUS.ABORTED) return true;
      if (v.status === VOYAGE_STATUS.DRAFT) {
        const last = new Date(v.lastDraftUpdatedAt || v.draftStartedAt || v.createdAt || 0).getTime();
        if (!Number.isFinite(last)) return false;
        return (now - last) > TWENTY_FOUR_HOURS_MS;
      }
      return false;
    });
    // Most recently updated first.
    return archived.sort((a, b) => {
      const at = new Date(a.updatedAt || a.lastDraftUpdatedAt || 0).getTime();
      const bt = new Date(b.updatedAt || b.lastDraftUpdatedAt || 0).getTime();
      return bt - at;
    });
  }, [voyages]);

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Archive className="h-10 w-10 text-slate-300 mb-3" aria-hidden="true" />
        <p className="text-sm text-slate-500">No archived or aborted voyages.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-200" role="list">
      {rows.map(v => (
        <div key={v.id} role="listitem" className="flex items-center justify-between gap-3 py-3 px-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-slate-900 truncate">
                {v.voyageNumber || 'Voyage'}
              </span>
              <StatusBadge status={v.status} />
            </div>
            <p className="text-xs text-slate-500 truncate">
              {(v.from?.name || '—')} → {(v.to?.name || '—')}
              <span className="mx-2">·</span>
              updated {formatRelative(v.updatedAt || v.lastDraftUpdatedAt)}
              {v.aborted?.reason ? (
                <>
                  <span className="mx-2">·</span>
                  reason: {v.aborted.reason}
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {v.status === VOYAGE_STATUS.DRAFT && (
              <>
                <button
                  type="button"
                  onClick={() => typeof onResume === 'function' && onResume(v.id)}
                  className="inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  <RefreshCcw className="h-4 w-4" aria-hidden="true" />
                  Resume
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const ok = typeof window !== 'undefined'
                      ? window.confirm('Permanently cancel this draft? It will move to Aborted.')
                      : true;
                    if (ok && typeof onCancelPermanently === 'function') {
                      onCancelPermanently(v.id);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium rounded-md transition-colors"
                >
                  <Ban className="h-4 w-4" aria-hidden="true" />
                  Cancel permanently
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ArchivedAbortedVoyagesTab;
